import React, { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchLaptops, generateLaptopsCsv } from '@/services/laptops';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { Laptop } from '@/types/models';
import { groupLaptops, GroupedLaptop, formatGroupPriceRange } from '@/lib/laptopGrouping';
import { TableSkeleton } from '@/components/common/Skeleton';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { Pagination } from '@/components/common/Pagination';
import { SearchInput } from '@/components/common/SearchInput';
import { Modal } from '@/components/common/Modal';
import { LaptopForm } from '@/features/laptops/LaptopForm';
import { LaptopDetail } from '@/features/laptops/LaptopDetail';
import { SetGroupPriceModal } from '@/features/laptops/SetGroupPriceModal';
import { LaptopSpecExtractorModal } from '@/features/ai/LaptopSpecExtractorModal';
import { ExtractedLaptopSpecs } from '@/services/aiExtraction';
import { useAuth } from '@/context/AuthContext';
import { generateLaptopQrLabelPdf } from '@/lib/qrLabelPdf';
import { compareByStockId } from '@/lib/stockIdSort';
import {
  Laptop as LaptopIcon,
  Plus,
  Download,
  Cpu,
  Sparkles,
  ChevronDown,
  ChevronRight,
  Layers,
  List,
  Tag,
  QrCode,
  Loader2,
} from 'lucide-react';
import { PAGE_SIZE } from '@/lib/constants';

const GROUPED_PAGE_SIZE = 10;

export const LaptopsPage: React.FC = () => {
  const { hasPermission, role } = useAuth();
  const isOwner = role === 'owner';
  const isStaff = !hasPermission('purchases');
  const canViewCosts = hasPermission('purchases');

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('current_stock');
  const [screenFilter, setScreenFilter] = useState<'all' | 'touch' | 'nontouch' | 'unknown'>('all');
  const [brandFilter] = useState('');
  const [conditionFilter, setConditionFilter] = useState('');
  const [archiveFilter, setArchiveFilter] = useState<'active' | 'archived' | 'all'>('active');
  const [viewMode, setViewMode] = useState<'grouped' | 'list'>('grouped');
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  // Independent pagination for Grouped mode (10 groups/page) and Flat mode (25 units/page)
  const [groupedPage, setGroupedPage] = useState(0);
  const [flatPage, setFlatPage] = useState(0);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const [aiSpecs, setAiSpecs] = useState<ExtractedLaptopSpecs | undefined>(undefined);
  const [selectedLaptopId, setSelectedLaptopId] = useState<string | null>(null);
  const [groupPriceTarget, setGroupPriceTarget] = useState<GroupedLaptop | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [qrGenerating, setQrGenerating] = useState(false);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback((laptops: Laptop[]) => {
    setSelectedIds((prev) => {
      const allSelected = laptops.every((l) => prev.has(l.id));
      if (allSelected) {
        const next = new Set(prev);
        laptops.forEach((l) => next.delete(l.id));
        return next;
      } else {
        const next = new Set(prev);
        laptops.forEach((l) => next.add(l.id));
        return next;
      }
    });
  }, []);

  const handleSearchChange = useCallback((val: string) => {
    setSearch(val);
    setGroupedPage(0);
    setFlatPage(0);
    const timer = setTimeout(() => setDebouncedSearch(val), 400);
    return () => clearTimeout(timer);
  }, []);

  const handleViewModeChange = (mode: 'grouped' | 'list') => {
    setViewMode(mode);
    setGroupedPage(0);
    setFlatPage(0);
  };

  // Fetch the full filtered dataset to allow genuine base-model grouping before pagination
  const { data: queryResult, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.laptops.list({
      search: debouncedSearch,
      status: statusFilter,
      brand: brandFilter,
      condition: conditionFilter,
      archiveFilter,
      fetchAll: true,
    }),
    queryFn: () =>
      fetchLaptops({
        search: debouncedSearch,
        status: statusFilter,
        brand: brandFilter,
        condition: conditionFilter,
        archiveFilter,
        fetchAll: true,
        isStaff,
      }),
    placeholderData: (prev) => prev,
  });

  const allFilteredLaptops = useMemo(() => {
    let list = queryResult?.data ?? [];
    if (screenFilter === 'touch') {
      list = list.filter((l) => l.touchscreen === true);
    } else if (screenFilter === 'nontouch') {
      list = list.filter((l) => l.touchscreen === false);
    } else if (screenFilter === 'unknown') {
      list = list.filter((l) => l.touchscreen == null);
    }
    return list;
  }, [queryResult, screenFilter]);

  const totalPhysicalUnits = allFilteredLaptops.length;

  // 1. Group the COMPLETE filtered dataset first
  const allGroupedLaptops = useMemo(() => {
    return groupLaptops(allFilteredLaptops);
  }, [allFilteredLaptops]);

  const totalModelGroups = allGroupedLaptops.length;

  // 2. Paginate model groups for Grouped Mode (10 groups per page)
  const paginatedGroupedLaptops = useMemo(() => {
    const from = groupedPage * GROUPED_PAGE_SIZE;
    return allGroupedLaptops.slice(from, from + GROUPED_PAGE_SIZE);
  }, [allGroupedLaptops, groupedPage]);

  // 3. Paginate physical units for Flat Mode (25 units per page)
  const paginatedFlatLaptops = useMemo(() => {
    const from = flatPage * PAGE_SIZE;
    return allFilteredLaptops.slice(from, from + PAGE_SIZE);
  }, [allFilteredLaptops, flatPage]);

  const toggleGroupExpand = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleExportCsv = () => {
    if (allFilteredLaptops.length === 0) return;
    const csvContent = generateLaptopsCsv(allFilteredLaptops, canViewCosts);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `laptops_inventory_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrintQrLabels = useCallback(async () => {
    const laptopsToPrint = allFilteredLaptops
      .filter((l) => selectedIds.size === 0 || selectedIds.has(l.id))
      .sort(compareByStockId);
    if (laptopsToPrint.length === 0) return;
    setQrGenerating(true);
    try {
      await generateLaptopQrLabelPdf(
        laptopsToPrint.map((l) => ({
          id: l.id,
          stock_id: l.stock_id,
          serial_number: l.serial_number,
        }))
      );
    } finally {
      setQrGenerating(false);
    }
  }, [allFilteredLaptops, selectedIds]);

  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-brand-black flex items-center space-x-2">
            <LaptopIcon className="w-5 h-5 text-brand-green" />
            <span>Laptop Inventory</span>
          </h1>
          <p className="text-xs text-brand-muted mt-0.5">
            {totalPhysicalUnits > 0
              ? `${totalPhysicalUnits} physical units • ${totalModelGroups} base model groups`
              : 'Manage serialized laptop stock'}
          </p>
        </div>
        <div className="flex items-center space-x-2 shrink-0 flex-wrap gap-y-1.5">
          {/* View Mode Toggle */}
          <div className="inline-flex rounded-md shadow-xs bg-brand-bg border border-brand-border p-0.5 text-xs">
            <button
              type="button"
              onClick={() => handleViewModeChange('grouped')}
              className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded font-semibold transition-colors ${
                viewMode === 'grouped'
                  ? 'bg-white text-brand-black shadow-xs'
                  : 'text-brand-muted hover:text-brand-black'
              }`}
              title="Group physical laptops by base model/hardware (RAM/SSD independent)"
            >
              <Layers className="w-3.5 h-3.5 text-brand-green" />
              <span>Grouped Models</span>
            </button>
            <button
              type="button"
              onClick={() => handleViewModeChange('list')}
              className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded font-semibold transition-colors ${
                viewMode === 'list'
                  ? 'bg-white text-brand-black shadow-xs'
                  : 'text-brand-muted hover:text-brand-black'
              }`}
              title="View all physical serialized units individually"
            >
              <List className="w-3.5 h-3.5 text-brand-green" />
              <span>All Physical Units</span>
            </button>
          </div>

          <button
            onClick={handleExportCsv}
            className="inline-flex items-center space-x-1.5 px-3 py-1.5 border border-brand-border text-xs font-semibold rounded-md text-brand-black hover:bg-brand-bg transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            <span>Export CSV</span>
          </button>
          <button
            onClick={handlePrintQrLabels}
            disabled={qrGenerating || allFilteredLaptops.length === 0}
            className="inline-flex items-center space-x-1.5 px-3 py-1.5 border border-brand-border text-xs font-semibold rounded-md text-brand-black hover:bg-brand-bg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={selectedIds.size > 0 ? `Print QR labels for ${selectedIds.size} selected unit(s)` : 'Print QR labels for all visible units'}
          >
            {qrGenerating ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <QrCode className="w-3.5 h-3.5" />
            )}
            <span>
              {selectedIds.size > 0 ? `QR Labels (${selectedIds.size})` : 'Print QR Labels'}
            </span>
          </button>
          {canViewCosts && (
            <>
              <button
                onClick={() => setShowAiModal(true)}
                className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-brand-green/10 border border-brand-green/30 text-brand-green text-xs font-semibold rounded-md hover:bg-brand-green/20 transition-colors shadow-2xs"
                title="Extract laptop specs and serial from a photo"
              >
                <Sparkles className="w-3.5 h-3.5 text-brand-green" />
                <span>Scan Specs (AI)</span>
              </button>
              <button
                onClick={() => {
                  setAiSpecs(undefined);
                  setShowAddModal(true);
                }}
                className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-brand-green text-white text-xs font-semibold rounded-md hover:bg-brand-green-hover transition-colors shadow-sm"
                id="add-laptop-btn"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Add Laptop</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Search & Filters */}
      <div className={`grid grid-cols-1 ${isOwner ? 'sm:grid-cols-6' : 'sm:grid-cols-5'} gap-3`}>
        <div className="sm:col-span-2">
          <SearchInput
            value={search}
            onChange={handleSearchChange}
            placeholder="Search stock ID, serial, model, CPU..."
          />
        </div>
        {isOwner && (
          <div>
            <select
              value={archiveFilter}
              onChange={(e) => {
                setArchiveFilter(e.target.value as 'active' | 'archived' | 'all');
                setGroupedPage(0);
                setFlatPage(0);
              }}
              className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-md text-xs font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500"
            >
              <option value="active">Active Only (Default)</option>
              <option value="archived">Archived Records</option>
              <option value="all">All (Active + Archived)</option>
            </select>
          </div>
        )}
        <div>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setGroupedPage(0);
              setFlatPage(0);
            }}
            className="w-full px-3 py-2 bg-white border border-brand-border rounded-md text-xs font-medium text-brand-black focus:outline-none focus:ring-2 focus:ring-brand-green"
          >
            <option value="current_stock">Current Stock (Default)</option>
            <option value="all">All Statuses / History</option>
            <option value="in_stock">In Stock Only</option>
            <option value="reserved">Reserved</option>
            <option value="under_repair">Under Repair</option>
            <option value="sold">Sold</option>
            <option value="returned">Returned</option>
            <option value="faulty">Faulty</option>
            <option value="scrapped">Scrapped</option>
          </select>
        </div>
        <div>
          <select
            value={screenFilter}
            onChange={(e) => {
              setScreenFilter(e.target.value as 'all' | 'touch' | 'nontouch' | 'unknown');
              setGroupedPage(0);
              setFlatPage(0);
            }}
            className="w-full px-3 py-2 bg-white border border-brand-border rounded-md text-xs font-medium text-brand-black focus:outline-none focus:ring-2 focus:ring-brand-green"
          >
            <option value="all">All Screen Types</option>
            <option value="touch">Touchscreen Only</option>
            <option value="nontouch">Non-Touch Only</option>
            <option value="unknown">Unknown Screen</option>
          </select>
        </div>
        <div>
          <select
            value={conditionFilter}
            onChange={(e) => {
              setConditionFilter(e.target.value);
              setGroupedPage(0);
              setFlatPage(0);
            }}
            className="w-full px-3 py-2 bg-white border border-brand-border rounded-md text-xs font-medium text-brand-black focus:outline-none focus:ring-2 focus:ring-brand-green"
          >
            <option value="">All Conditions</option>
            <option value="new">New</option>
            <option value="excellent">Excellent</option>
            <option value="good">Good</option>
            <option value="fair">Fair</option>
            <option value="poor">Poor</option>
          </select>
        </div>
      </div>

      {/* Error */}
      {error && <ErrorAlert message={parseDatabaseError(error)} onRetry={() => refetch()} />}

      {/* Table */}
      <div className="bg-white rounded-lg border border-brand-border shadow-xs overflow-hidden">
        <div className="overflow-x-auto">
          {viewMode === 'grouped' ? (
            /* ── GROUPED BY BASE MACHINE PRESENTATION ── */
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-brand-bg border-b border-brand-border">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-brand-muted uppercase tracking-wider">
                    Base Model / Hardware Group
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-brand-muted uppercase tracking-wider hidden sm:table-cell">
                    Base Processor
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-brand-muted uppercase tracking-wider">
                    Physical Stock
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-brand-muted uppercase tracking-wider">
                    Asking Price
                  </th>
                  {canViewCosts && (
                    <th className="text-left px-4 py-3 text-xs font-semibold text-brand-muted uppercase tracking-wider hidden lg:table-cell">
                      Cost Range
                    </th>
                  )}
                  <th className="px-4 py-3 text-right text-xs font-semibold text-brand-muted uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>

              {isLoading ? (
                <TableSkeleton rows={8} cols={canViewCosts ? 6 : 5} />
              ) : allGroupedLaptops.length === 0 ? (
                <tbody>
                  <tr>
                    <td colSpan={canViewCosts ? 6 : 5}>
                      <div className="flex flex-col items-center justify-center py-16 text-center">
                        <div className="w-16 h-16 rounded-full bg-brand-green/10 flex items-center justify-center mb-4">
                          <LaptopIcon className="w-8 h-8 text-brand-green" />
                        </div>
                        <p className="text-base font-bold text-brand-black">No laptops found</p>
                        <p className="text-xs text-brand-muted mt-1 max-w-xs">
                          {debouncedSearch || statusFilter !== 'current_stock' || conditionFilter || screenFilter !== 'all' || archiveFilter !== 'active'
                            ? 'No inventory matches your active filters.'
                            : 'Add your first laptop to start tracking inventory.'}
                        </p>
                      </div>
                    </td>
                  </tr>
                </tbody>
              ) : (
                <tbody className="divide-y divide-brand-border">
                  {paginatedGroupedLaptops.map((group: GroupedLaptop) => {
                    const isExpanded = expandedKeys.has(group.key);
                    return (
                      <React.Fragment key={group.key}>
                        <tr
                          onClick={() => toggleGroupExpand(group.key)}
                          className={`hover:bg-brand-bg/80 cursor-pointer transition-colors ${
                            isExpanded ? 'bg-brand-bg/50 font-semibold' : ''
                          }`}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-3">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleGroupExpand(group.key);
                                }}
                                className="p-1 rounded text-brand-muted hover:text-brand-black hover:bg-brand-bg"
                                aria-label="Toggle units"
                              >
                                {isExpanded ? (
                                  <ChevronDown className="w-4 h-4 text-brand-green" />
                                ) : (
                                  <ChevronRight className="w-4 h-4" />
                                )}
                              </button>
                              <div>
                                <div className="flex items-center space-x-1.5 flex-wrap">
                                  <p className="font-bold text-brand-black text-sm">
                                    {group.brand} {group.model}
                                  </p>
                                  {group.touchscreen === true && (
                                    <span className="px-1.5 py-0.2 bg-blue-100 text-blue-800 text-[9px] font-bold rounded">
                                      Touch
                                    </span>
                                  )}
                                  {group.touchscreen === false && (
                                    <span className="px-1.5 py-0.2 bg-slate-100 text-slate-700 text-[9px] font-bold rounded">
                                      Non-Touch
                                    </span>
                                  )}
                                </div>
                                <p className="text-[11px] text-brand-muted">
                                  {group.units.length} physical {group.units.length === 1 ? 'unit' : 'units'} registered
                                  {group.hasArchived && ' • Includes archived'}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-xs text-brand-muted hidden sm:table-cell">
                            <p className="flex items-center space-x-1 font-medium text-brand-black">
                              <Cpu className="w-3.5 h-3.5 text-brand-green" />
                              <span>{group.cpu || 'Base CPU'}</span>
                            </p>
                            {group.gpuMixed ? (
                              <p className="text-[11px] text-amber-700 font-semibold">Discrete GPU: Mixed</p>
                            ) : group.gpu ? (
                              <p className="text-[11px] text-brand-muted">{group.gpu}</p>
                            ) : null}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-1.5 flex-wrap gap-y-1">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-800 border border-green-200">
                                {group.inStockQuantity} In Stock
                              </span>
                              {group.totalQuantity - group.inStockQuantity > 0 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                                  {group.totalQuantity - group.inStockQuantity} other
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-bold text-brand-green text-sm">
                              {formatGroupPriceRange(group.minPrice, group.maxPrice)}
                            </span>
                          </td>
                          {canViewCosts && (
                            <td className="px-4 py-3 text-xs text-brand-muted hidden lg:table-cell">
                              {group.minCost != null && group.maxCost != null
                                ? formatGroupPriceRange(group.minCost, group.maxCost)
                                : '—'}
                            </td>
                          )}
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end space-x-2">
                              {canViewCosts && group.inStockQuantity > 0 && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setGroupPriceTarget(group);
                                  }}
                                  className="inline-flex items-center space-x-1 px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-800 text-[10px] font-bold rounded border border-slate-300 transition-colors shadow-2xs"
                                  title="Bulk set asking price for all in-stock units in this model group"
                                >
                                  <Tag className="w-3 h-3 text-brand-green" />
                                  <span>Set Group Price</span>
                                </button>
                              )}
                              <span className="inline-flex items-center space-x-1 text-xs font-bold text-brand-green hover:underline">
                                <span>{isExpanded ? 'Collapse' : `Units (${group.units.length})`}</span>
                                {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                              </span>
                            </div>
                          </td>
                        </tr>

                        {/* ── EXPANDED SERIALIZED PHYSICAL UNITS SUB-TABLE ── */}
                        {isExpanded && (
                          <tr>
                            <td colSpan={canViewCosts ? 6 : 5} className="p-0 bg-brand-bg/40 border-y border-brand-border">
                              <div className="p-3 pl-8 sm:pl-12 space-y-2">
                                <div className="flex items-center justify-between">
                                  <p className="text-[11px] font-bold text-brand-muted uppercase tracking-wider">
                                    Physical Serialized Units in Group ({group.units.length})
                                  </p>
                                  <p className="text-[10px] text-brand-muted">
                                    RAM/SSD can be configured at point of sale. Click any unit to view details.
                                  </p>
                                </div>
                                <div className="bg-white rounded-lg border border-brand-border overflow-hidden shadow-xs">
                                  <table className="w-full text-xs">
                                     <thead className="bg-brand-bg border-b border-brand-border text-brand-muted font-semibold">
                                      <tr>
                                        <th className="px-3 py-2 w-8">
                                          <input
                                            type="checkbox"
                                            title="Select all in group"
                                            checked={group.units.every((u) => selectedIds.has(u.id))}
                                            onChange={(e) => {
                                              e.stopPropagation();
                                              toggleSelectAll(group.units as Laptop[]);
                                            }}
                                            className="rounded border-brand-border accent-brand-green"
                                          />
                                        </th>
                                        <th className="text-left px-3 py-2">Stock ID</th>
                                        <th className="text-left px-3 py-2">Serial Number</th>
                                        <th className="text-left px-3 py-2">RAM / SSD</th>
                                        <th className="text-left px-3 py-2">Discrete GPU</th>
                                        <th className="text-left px-3 py-2">Condition</th>
                                        <th className="text-left px-3 py-2">Status</th>
                                        <th className="text-right px-3 py-2">Asking Price</th>
                                        {canViewCosts && <th className="text-right px-3 py-2">Cost</th>}
                                        <th className="px-3 py-2 text-right">Action</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-brand-border">
                                      {group.units.map((unit) => (
                                        <tr
                                          key={unit.id}
                                          onClick={() => setSelectedLaptopId(unit.id)}
                                          className={`hover:bg-slate-50 cursor-pointer transition-colors ${
                                            unit.archived_at ? 'opacity-60 bg-slate-50' : ''
                                          }`}
                                        >
                                          <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                                            <input
                                              type="checkbox"
                                              checked={selectedIds.has(unit.id)}
                                              onChange={() => toggleSelect(unit.id)}
                                              className="rounded border-brand-border accent-brand-green"
                                            />
                                          </td>
                                          <td className="px-3 py-2 font-mono font-bold text-brand-black">
                                            <div className="flex items-center space-x-1.5">
                                              <span>{unit.stock_id}</span>
                                              {unit.archived_at && (
                                                <span className="px-1 py-0.2 bg-slate-200 text-slate-700 text-[9px] font-bold rounded">
                                                  Archived
                                                </span>
                                              )}
                                            </div>
                                          </td>
                                          <td className="px-3 py-2 font-mono text-brand-muted">
                                            {unit.serial_number || '—'}
                                          </td>
                                          <td className="px-3 py-2 text-brand-black">
                                            <div className="flex items-center space-x-1">
                                              <span>{unit.ram || '—'}</span>
                                              <span className="text-brand-muted">•</span>
                                              <span>{unit.storage || '—'}</span>
                                            </div>
                                          </td>
                                          <td className="px-3 py-2 text-brand-muted text-[11px]">
                                            {unit.gpu || '—'}
                                          </td>
                                          <td className="px-3 py-2">
                                            <span className="inline-block px-1.5 py-0.5 text-[10px] font-semibold uppercase rounded bg-slate-100 text-slate-700">
                                              {unit.condition}
                                            </span>
                                          </td>
                                          <td className="px-3 py-2">
                                            <span
                                              className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase rounded-full ${
                                                unit.status === 'in_stock'
                                                  ? 'bg-green-100 text-green-800'
                                                  : unit.status === 'sold'
                                                  ? 'bg-slate-100 text-slate-800'
                                                  : unit.status === 'reserved'
                                                  ? 'bg-amber-100 text-amber-800'
                                                  : 'bg-red-100 text-red-800'
                                              }`}
                                            >
                                              {unit.status.replace('_', ' ')}
                                            </span>
                                          </td>
                                          <td className="px-3 py-2 text-right font-bold text-brand-green">
                                            Rs. {(unit.asking_price ?? 0).toLocaleString('en-PK')}
                                          </td>
                                          {canViewCosts && (
                                            <td className="px-3 py-2 text-right font-mono text-brand-muted">
                                              Rs. {(unit.landed_cost ?? unit.purchase_price ?? 0).toLocaleString('en-PK')}
                                            </td>
                                          )}
                                          <td className="px-3 py-2 text-right font-semibold text-brand-green hover:underline">
                                            View Unit
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              )}
            </table>
          ) : (
            /* ── FLAT SERIALIZED UNIT PRESENTATION ── */
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-brand-bg border-b border-brand-border">
                  <th className="px-4 py-3 w-8">
                    <input
                      type="checkbox"
                      title="Select all visible units"
                      checked={paginatedFlatLaptops.length > 0 && paginatedFlatLaptops.every((l) => selectedIds.has(l.id))}
                      onChange={() => toggleSelectAll(paginatedFlatLaptops)}
                      className="rounded border-brand-border accent-brand-green"
                    />
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-brand-muted uppercase tracking-wider">
                    Stock ID & Serial
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-brand-muted uppercase tracking-wider">
                    Brand & Model
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-brand-muted uppercase tracking-wider hidden md:table-cell">
                    RAM / SSD
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-brand-muted uppercase tracking-wider hidden lg:table-cell">
                    Discrete GPU
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-brand-muted uppercase tracking-wider hidden sm:table-cell">
                    Condition
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-brand-muted uppercase tracking-wider">
                    Status
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-brand-muted uppercase tracking-wider">
                    Asking Price
                  </th>
                  {canViewCosts && (
                    <th className="text-right px-4 py-3 text-xs font-semibold text-brand-muted uppercase tracking-wider hidden xl:table-cell">
                      Landed Cost
                    </th>
                  )}
                  <th className="px-4 py-3 text-right text-xs font-semibold text-brand-muted uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>

              {isLoading ? (
                <TableSkeleton rows={8} cols={canViewCosts ? 10 : 9} />
              ) : allFilteredLaptops.length === 0 ? (
                <tbody>
                  <tr>
                    <td colSpan={canViewCosts ? 10 : 9}>
                      <div className="flex flex-col items-center justify-center py-16 text-center">
                        <div className="w-16 h-16 rounded-full bg-brand-green/10 flex items-center justify-center mb-4">
                          <LaptopIcon className="w-8 h-8 text-brand-green" />
                        </div>
                        <p className="text-base font-bold text-brand-black">No laptops found</p>
                        <p className="text-xs text-brand-muted mt-1 max-w-xs">
                          {debouncedSearch || statusFilter !== 'current_stock' || conditionFilter || screenFilter !== 'all' || archiveFilter !== 'active'
                            ? 'No inventory matches your active filters.'
                            : 'Add your first laptop to start tracking inventory.'}
                        </p>
                      </div>
                    </td>
                  </tr>
                </tbody>
              ) : (
                <tbody className="divide-y divide-brand-border">
                  {paginatedFlatLaptops.map((laptop: Laptop) => (
                    <tr
                      key={laptop.id}
                      onClick={() => setSelectedLaptopId(laptop.id)}
                      className={`hover:bg-brand-bg/80 cursor-pointer transition-colors ${
                        laptop.archived_at ? 'opacity-60 bg-slate-50' : ''
                      }`}
                    >
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(laptop.id)}
                          onChange={() => toggleSelect(laptop.id)}
                          className="rounded border-brand-border accent-brand-green"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-1.5">
                          <span className="font-mono font-bold text-brand-black text-xs">
                            {laptop.stock_id}
                          </span>
                          {laptop.archived_at && (
                            <span className="px-1.5 py-0.2 bg-slate-200 text-slate-700 text-[9px] font-bold rounded">
                              Archived
                            </span>
                          )}
                        </div>
                        {laptop.serial_number && (
                          <p className="text-[11px] font-mono text-brand-muted">
                            S/N: {laptop.serial_number}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-1.5 flex-wrap">
                          <p className="font-bold text-brand-black text-xs">
                            {laptop.brand} {laptop.model}
                          </p>
                          {laptop.touchscreen === true && (
                            <span className="px-1.5 py-0.2 bg-blue-100 text-blue-800 text-[9px] font-bold rounded">
                              Touch
                            </span>
                          )}
                          {laptop.touchscreen === false && (
                            <span className="px-1.5 py-0.2 bg-slate-100 text-slate-700 text-[9px] font-bold rounded">
                              Non-Touch
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-brand-muted">{laptop.cpu || 'Base CPU'}</p>
                      </td>
                      <td className="px-4 py-3 text-xs text-brand-black hidden md:table-cell">
                        <p className="font-medium">
                          {laptop.ram || '—'} • {laptop.storage || '—'}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-xs text-brand-muted hidden lg:table-cell font-mono">
                        {laptop.gpu || '—'}
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <span className="inline-block px-2 py-0.5 text-[10px] font-semibold uppercase rounded bg-slate-100 text-slate-700">
                          {laptop.condition}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase rounded-full ${
                            laptop.status === 'in_stock'
                              ? 'bg-green-100 text-green-800'
                              : laptop.status === 'sold'
                              ? 'bg-slate-100 text-slate-800'
                              : laptop.status === 'reserved'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {laptop.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-brand-green text-xs">
                        Rs. {(laptop.asking_price ?? 0).toLocaleString('en-PK')}
                      </td>
                      {canViewCosts && (
                        <td className="px-4 py-3 text-right font-mono text-brand-muted text-xs hidden lg:table-cell">
                          Rs. {(laptop.landed_cost ?? laptop.purchase_price ?? 0).toLocaleString('en-PK')}
                        </td>
                      )}
                      <td className="px-4 py-3 text-right text-xs font-semibold text-brand-green hover:underline">
                        View
                      </td>
                    </tr>
                  ))}
                </tbody>
              )}
            </table>
          )}
        </div>

        {/* Dynamic Pagination */}
        {viewMode === 'grouped' ? (
          <Pagination
            page={groupedPage}
            totalCount={totalModelGroups}
            pageSize={GROUPED_PAGE_SIZE}
            onPageChange={setGroupedPage}
          />
        ) : (
          <Pagination
            page={flatPage}
            totalCount={totalPhysicalUnits}
            pageSize={PAGE_SIZE}
            onPageChange={setFlatPage}
          />
        )}
      </div>

      {/* Add Laptop Modal */}
      {showAddModal && (
        <Modal
          isOpen={showAddModal}
          title="Add New Laptop"
          onClose={() => setShowAddModal(false)}
        >
          <LaptopForm
            initialSpecs={aiSpecs}
            onSuccess={() => {
              setShowAddModal(false);
              setAiSpecs(undefined);
              void refetch();
            }}
            onCancel={() => {
              setShowAddModal(false);
              setAiSpecs(undefined);
            }}
          />
        </Modal>
      )}

      {/* AI Spec Extractor Modal */}
      {showAiModal && (
        <Modal isOpen={showAiModal} title="AI Laptop Spec Extraction" onClose={() => setShowAiModal(false)}>
          <LaptopSpecExtractorModal
            onCancel={() => setShowAiModal(false)}
            onConfirm={(extracted) => {
              setShowAiModal(false);
              setAiSpecs(extracted);
              setShowAddModal(true);
            }}
          />
        </Modal>
      )}

      {/* Bulk Set Group Asking Price Modal */}
      {groupPriceTarget && (
        <SetGroupPriceModal
          group={groupPriceTarget}
          isOpen={!!groupPriceTarget}
          onClose={() => setGroupPriceTarget(null)}
          onSuccess={() => {
            void refetch();
          }}
        />
      )}

      {/* Laptop Detail Modal */}
      {selectedLaptopId && (
        <LaptopDetail
          laptopId={selectedLaptopId}
          onClose={() => setSelectedLaptopId(null)}
        />
      )}
    </div>
  );
};
